import { useEffect, useRef } from 'react';
import { getAccessToken, handleLogout, isTokenExpired } from '../utils/tokenUtils';

/**
 * Custom hook to detect session timeout and reload/redirect user
 * Monitors token expiry and user activity
 */
const useSessionTimeout = (timeoutWarningMs = 60000) => {
    const warningShownRef = useRef(false);

    useEffect(() => {
        const accessToken = getAccessToken();
        
        if (!accessToken) {
            return;
        }

        // Check token expiry periodically (every 30 seconds)
        const checkTokenInterval = setInterval(() => {
            const token = getAccessToken();
            
            if (!token) {
                // No token found, redirect to login
                handleLogout();
                return;
            }

            if (isTokenExpired(token)) {
                // Token expired, log out and reload
                console.warn('Session expired. Redirecting to login...');
                handleLogout();
                // Reload page to ensure clean state
                window.location.reload();
            }
        }, 30000); // Check every 30 seconds

        // Detect user inactivity - reset timer on any user action
        let inactivityTimer = null;

        const resetInactivityTimer = () => {
            if (inactivityTimer) {
                clearTimeout(inactivityTimer);
            }

            warningShownRef.current = false;

            // Set inactivity timeout (default: 30 minutes = 1800000ms)
            const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

            inactivityTimer = setTimeout(() => {
                console.warn('User session inactive. Logging out...');
                handleLogout();
                window.location.reload();
            }, SESSION_TIMEOUT_MS);
        };

        // Activity event listeners
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        
        events.forEach(event => {
            document.addEventListener(event, resetInactivityTimer, true);
        });

        // Initialize timer on mount
        resetInactivityTimer();

        // Cleanup function
        return () => {
            clearInterval(checkTokenInterval);
            if (inactivityTimer) {
                clearTimeout(inactivityTimer);
            }
            events.forEach(event => {
                document.removeEventListener(event, resetInactivityTimer, true);
            });
        };
    }, [timeoutWarningMs]);
};

export default useSessionTimeout;
